import type { FC } from 'react';
import { lazy, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import type { AuctionsOfAuctionTileParams } from 'src/apis/transport-auction/types';
import { useAuctionTile } from 'src/apis/transport-auction/use-auction-tile';
import { useAuctionsOfAuctionTile } from 'src/apis/transport-auction/use-auctions-of-auction-tile';
import {
  useTransportAuctionServiceRoot,
  type OptionalTransportAuctionServiceRootRequest,
} from 'src/apis/transport-auction/use-transport-auction-service-root';
import { PageSpinner } from 'src/components/spinner-container';
import type { Successful } from 'src/hooks/use-axios';
import { useAuctionsPageParams } from 'src/pages/transport/auctions/tiles/use-page-params';
import { isConstraintViolation } from 'src/utils/is-constraint-violation';

const Page = lazy(() => import('./page'));
const ErrorPage = lazy(() => import('./error'));

export const RunningAuctionsOverviewTAS: FC = () => {
  const transportAuctionServiceRoot = useTransportAuctionServiceRoot();

  if (!transportAuctionServiceRoot.response) return <PageSpinner />;

  return <LoadData transportAuctionServiceRoot={transportAuctionServiceRoot} />;
};

const LoadData: FC<{
  transportAuctionServiceRoot: Successful<OptionalTransportAuctionServiceRootRequest>;
}> = ({ transportAuctionServiceRoot }) => {
  const { tileId } = useParams<{ tileId: string }>();
  const pageParams = useAuctionsPageParams();

  const tile = useAuctionTile({
    root: transportAuctionServiceRoot,
    tileId,
  });

  const auctionsParams = useMemo(
    () =>
      ({
        limit: pageParams.value.pageSize,
        offset: pageParams.value.start,
        sortBy: pageParams.value.sortBy,
        sortDirection: pageParams.value.sortDirection,
        quickFilterId: pageParams.value.quickFilterId,
        favouritesOnly: pageParams.value.favouritesOnly,
        ownAuctionsOnly: pageParams.value.ownAuctionsOnly,
        networkPointOrBundleId: pageParams.value.networkPointOrBundleId.map(
          (option) => option.value
        ),
        direction: pageParams.value.direction,
        operatorId: pageParams.value.operatorId.map((option) => option.value),
        capacityCategoryId: pageParams.value.capacityCategoryId.map(
          (option) => option.value
        ),
        marketableCapacityMin: pageParams.value.marketableCapacityMin,
        marketableCapacityMax: pageParams.value.marketableCapacityMax,
        runtimeIntersectionFilterStart:
          pageParams.value.runtimeIntersectionFilterStart,
        runtimeIntersectionFilterEnd:
          pageParams.value.runtimeIntersectionFilterEnd,
        startingPriceMin: pageParams.value.startingPriceMin,
        startingPriceMax: pageParams.value.startingPriceMax,
        marketTransitionPriceMin: pageParams.value.marketTransitionPriceMin,
        marketTransitionPriceMax: pageParams.value.marketTransitionPriceMax,
        assignedAuctionId: pageParams.value.assignedAuctionId,
      }) satisfies AuctionsOfAuctionTileParams,
    [pageParams.value]
  );
  const auctions = useAuctionsOfAuctionTile({
    root: transportAuctionServiceRoot,
    tileId,
    params: auctionsParams,
  });

  if (tile.error) return <ErrorPage error={tile.error} />;
  if (auctions.error && !isConstraintViolation(auctions.error))
    return <ErrorPage error={auctions.error} />;

  if (!tile.response || !auctions.loaded) return <PageSpinner />;

  return (
    <Page
      transportAuctionServiceRoot={transportAuctionServiceRoot}
      tile={tile}
      auctions={auctions}
      pageParams={pageParams}
    />
  );
};
